import React, { useEffect, useCallback, useState} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {Content, FilterBar, IFilterDropdownConfig, IFilterItem, IFilterResult, ISearchFilter, PageHeader, SortDropdown, TypeTable} from 'scorer-ui-kit';
import { ITableColumnConfig, ITypeTableData, IRowData } from 'scorer-ui-kit/dist/Tables';
import { useDevices } from '../hooks/useDevices';
import { emptyTable, getTimeSince, sortDevices } from '../util';
import Pagination from '../components/Pagination';
import { Device, DeviceStatus, getDeviceStatus } from '../lib/api_config';
import  Noimg  from '../images/no-image.png';
import { useHistory, useLocation } from 'react-router-dom';

const PageContent = styled(Content)`
  margin-bottom: 20px;
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
`;

interface Icons {
  [key:string]: string
}
const DeviceIcons : Icons = {
  'folder': 'DevicesFolder',
  'stream': 'Camera',
  'ip-camera': 'Camera',
  'raspi': 'Camera',
  'safie-camera': 'DevicesScorerCamera',
  'undefined': 'Camera'
};

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const HeaderWrapper =styled.div`
  line-height: 35px;
  vertical-align: middle;
`;

const IntroductionText = styled.p`
  text-align: left;
  font-size: 14px;
  font-weight: 300;
  line-height: 25px;
  -webkit-text-decoration: none;
  text-decoration: none;
  color: hsla(207,5%,56.7%,1.000);
`;

const SortingWrapper = styled.div`
  height: 128px;
  padding-top: 46px;
`;

const FilterWrapper = styled.div`
  flex-grow: 1;
  margin-top: 15px;
  > div > div: nth-child(3){
    > div : nth-child(2) {
      pointer-events: auto;
    }
  }
  > div > div > div: nth-child(2){
    max-width: 550px;
  }
  @media screen and (max-width: 1400px){
    > div > div > div: nth-child(2){
      max-width: 450px;
    }
  }
  & div > div > div > div > div > div {
    max-height: 185px;
  }
`;

const FilterResultsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 60px;
`;

interface DeviceStatusMap {
  [deviceID: string]: DeviceStatus
}

interface INewParams {
  cameraName?: string | null;
  tag?: string | null;
  sortBy?: string | null;
  pageSize?: string | null;
}

interface IFilterData {
  cameraName: string;
  tag: string;
  paramsLoaded: string;
  pageSize?: string | null;
}

const FILTER_DATA: IFilterData = { cameraName: '', tag: '', pageSize: '', paramsLoaded: '' };

function Cameras() {

  const [rowData, setRowData] =  useState<ITypeTableData>([]);
  const {state: {devices, loading}, actions:{fetchDevices}} = useDevices();
  const [statusMap, setStatusMap] = useState<DeviceStatusMap>({});
  const { t } = useTranslation(['CameraList', 'Common']);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(rowData.length);
  const [rows, setRows] =  useState<ITypeTableData>([]);
  const [filterValues, setFilterValues] = useState({ ...FILTER_DATA });
  const {replace} = useHistory();
  const params = useLocation().search;
  const [historyParams] = useState(params);
  const [sorting, setSorting] = useState({ value: 'cameraName', ascending: false, text: t('cameraName') });
  const sizeOptions = [10, 20, 50, 100];
  const [deviceTags, setDeviceTags ] = useState<any[]>([]);
  const [filterData, setFilterData] = useState<Device[]>([]);
  const [sortType, setSortType] = useState('desc');

  const tableColumns: ITableColumnConfig[] = [
    {
      header: t('cameraName'),
      sortable: false,
      cellStyle: 'firstColumn'
    },
    {
      header: t('lastActivity'),
      sortable: false,
      cellStyle: 'firstColumn'
    },
    {
      header: t('tags'),
      sortable: false,
      cellStyle: 'firstColumn'
    }
  ];

  const sortByList = [
    {
      value: 'cameraName',
      text: t('cameraName')
    },
  ];

  const dropdownConfig: IFilterDropdownConfig[] = [
    {
      id: 'tag',
      buttonText: t('tags'),
      selected: filterValues.tag ? { text: filterValues.tag, value: filterValues.tag } : undefined,
      list: deviceTags ?? [],
      buttonIcon: 'MetaTags',
      maxDisplayedItems: deviceTags.length,
      optionType: 'radio',
      disabled: deviceTags.length === 0 ? true : false
    },
  ];

  const getSearchConfig = useCallback((): ISearchFilter[] => {
    return [
      {
        id: 'cameraName',
        placeholder: t('searchByCameraName'),
        name: t('cameraName'),
        disabled: devices.length === 0 ? true : false,
        selected: filterValues.cameraName ? { text: filterValues.cameraName, value: filterValues.cameraName } : undefined
      }
    ];
  }, [filterValues, t, devices]);

  const setParams = useCallback(() => {
    const params = '?' +
      `${filterValues.cameraName === '' ? '' : '&cameraName=' + filterValues.cameraName}` +
      `${pageSize === 10 ? '' : '&pageSize=' + pageSize}` +
      `${!sorting.ascending && sorting.value === 'cameraName' ? '' : '&sortBy=' + JSON.stringify(sorting)}` +
      `${filterValues.tag === '' ? '' : '&tag=' + filterValues.tag}`;

    if (params === '?') {
      return '';
    }
    return params;
  }, [filterValues, sorting, pageSize]);

  useEffect(() => {
    replace(window.location.pathname + setParams());
  }, [replace, setParams, filterValues]);

  const fetchHistoryParams = useCallback(() => {
    const params = new URLSearchParams(historyParams);
    const newParams: INewParams = {};
    newParams.cameraName = params.get('cameraName');
    newParams.sortBy = params.get('sortBy');
    newParams.tag = params.get('tag');
    newParams.pageSize = params.get('pageSize');

    const searchFilter = { ...FILTER_DATA };
    searchFilter.cameraName = newParams.cameraName ?? '';
    searchFilter.tag = newParams.tag ?? '';
    setSorting(prev => (newParams.sortBy ? JSON.parse(newParams.sortBy) : prev));
    if(newParams.sortBy) {
      const sortValue = Object.values(JSON.parse(newParams.sortBy));
      if(sortValue) {
        setSortType('asc');
      } else {
        setSortType('desc');
      }
    } else {
      setSortType('desc');
    }
    if(newParams.pageSize){
      setPageSize(prev => (newParams.pageSize ? JSON.parse(newParams.pageSize) : prev));
    }
    searchFilter.paramsLoaded = 'true';
    setFilterValues(searchFilter);
  }, [historyParams]);

  useEffect(() => {
    fetchHistoryParams();
  }, [fetchHistoryParams]);

  const getTagList = useCallback(() => {
    let tagList: string[] = [];
    tagList = devices.map((item) => item.tags).flat();
    const newArr: string[] = [];
    for (let i = 0; i < tagList.length; i++) {
      if (newArr.indexOf(tagList[i]) === -1) {
        newArr.push(tagList[i]);
      }
    }
    return newArr.map((value: string) => ({ value: value, text: value.length > 28 ? value.substring(0, 28) + '...' : value }));
  }, [devices]);

  const filterCameraData = useCallback(() => {
    const filterData = devices.filter((item: Device) => {
      if (item.name.toLowerCase().includes(filterValues.cameraName.toLowerCase()) && (item.tags.includes(filterValues.tag) || filterValues.tag === '')) {
        return item;
      } else {
        return false;
      }
    });
    const sortedDevices = sortDevices(filterData, sortType, 'name');
    return sortedDevices;
  }, [devices, filterValues, sortType]);

  useEffect(() => {
    const data = filterCameraData();
    setTotalPages(Math.ceil(data.length / pageSize));
    const newRows = data.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    setFilterData(newRows);
  },[filterCameraData, pageSize, currentPage]);

  const generateRowData = useCallback(() : ITypeTableData => {
    const filteredRows = filterData.reduce((rows, {name, lid, tags, type}) => {
      const {lastSnapshot: {signedUrl='-', time='-'}={}} = statusMap[lid]||{};
      const row : IRowData = {
        header: {
          image: signedUrl ? signedUrl : Noimg,
          icon: DeviceIcons[type],
          mediaType: 'img',
          mediaUrl: signedUrl,
        },
        columns: [
          { text: name, href: `/cameras/${lid}` },
          { text: getTimeSince(time) },
          { text: tags.join(', ')}
        ]
      };
      rows.push(row);
      return rows;
    }, [] as ITypeTableData);
    return filteredRows;
  },[statusMap, filterData]);

  const fetchDeviceStatus = useCallback(async () => {
    const statusMap: DeviceStatusMap = {};
    const deviceStatusPromises = devices.map(async({lid}) => {
      const status = await getDeviceStatus(lid);
      return {deviceID: lid,status};
    });
    for await(const {deviceID, status} of deviceStatusPromises){
      statusMap[deviceID] = status;
      setStatusMap({...statusMap});
    }
  }, [devices]);

  useEffect(()=>{
    fetchDevices();
  },[fetchDevices]);

  useEffect(()=>{
    fetchDeviceStatus();
  },[devices, fetchDeviceStatus]);

  useEffect(()=> {
    setDeviceTags(getTagList);
    setRowData(generateRowData);
  }, [generateRowData, getTagList, t]);

  useEffect(() => {
    setRows(rowData);
  }, [rowData]);

  const onSizePerPageChange = useCallback((size: number) => {
    setPageSize(size);
  }, [setPageSize]);

  const onPageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, [setCurrentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  const handleFilter = useCallback((res: IFilterResult[]) => {
    const searchFilter: any = { ...FILTER_DATA };
    res.forEach((item) => {
      searchFilter[item.id] = (item.selected as IFilterItem).value as string;
    });
    setFilterValues({ ...searchFilter, paramsLoaded: 'true' });
  }, []);

  const onChangeSorting1 = useCallback((event, ascending) => {
    setSorting({ value: event.value.toString(), text: event.text, ascending });
    if(ascending){
      setSortType('asc');
    } else {
      setSortType('desc');
    }
  }, []);

  useEffect(() => {
    const data = filterCameraData();
    setTotalPages(Math.ceil(data.length / pageSize));
    const newRows = data.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    setFilterData(newRows);
  },[filterCameraData, pageSize, currentPage]);

  return (
    <PageContent>
      <HeaderWrapper>
        <PageHeader title={t('CameraList:cameras')} icon='Camera' updateDocTitle={false} />
        <IntroductionText>{t('CameraList:introductionText')}</IntroductionText>
      </HeaderWrapper>
      <FilterContainer>
        <FilterWrapper>
          {filterValues.paramsLoaded === 'true' &&
            <FilterBar
              resultTextTemplate={`${t('Common:showingResults')} (${filterData.length}):`} filtersTitle={t('Common:filters') + ':'}
              totalResults={filterData.length} searchersConfig={getSearchConfig()} dropdownsConfig={dropdownConfig} onChangeCallback={handleFilter}
              clearText={t('Common:clearAll')}
            />}
        </FilterWrapper>
        <SortingWrapper>
          <SortDropdown
            ascendingText={t('Common:sorting.ascending')}
            buttonText={t('Common:sortedBy') + ' ' + t('CameraList:cameraName')}
            descendingText={t('Common:sorting.descending')}
            isSortAscending={sorting.ascending}
            list={sortByList}
            onSelect={onChangeSorting1}
            selected={{ text: t('CameraList:cameraName'), value: sorting.value }} 
          />
        </SortingWrapper>
      </FilterContainer>
      <FilterResultsContainer />
      <TypeTable
        columnConfig={tableColumns}
        rows={(!loading && devices.length === 0) ? emptyTable : rows}
        hasTypeIcon
        hasThumbnail
        isLoading={loading}
        emptyTableText={t('noCamerasFound')}
        loadingText=''
      />
      <PaginationContainer>
        <Pagination
          pageSizeOptions={sizeOptions}
          totalPages={totalPages}
          defaultPage={currentPage}
          defaultPageSize={pageSize}
          onPageSizeChange={onSizePerPageChange}
          onPageChange={onPageChange}
          pageSizeText={t('Common:pagination.itemsPerPage')}
          prevPageText='<'
          nextPageText='>'
          firstPageText='<<'
          lastPageText='>>'
        />
      </PaginationContainer>
    </PageContent>
  );
}

export default Cameras;
